// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// In-app Help Center — a searchable guide to every part of OpenEstimate.
// Self-contained modal: each host renders <HelpCenter T onClose/> when open.

import React, { useState, useMemo } from "react";
import type { Theme } from "../lib/theme";
import * as Icon from "./OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface Article {
  id: string;
  title: string;
  blurb: string;
  steps?: string[];
  tips?: string[];
}

interface Section {
  id: string;
  label: string;
  articles: Article[];
}

const SECTIONS: Section[] = [
  {
    id: "start",
    label: "Getting Started",
    articles: [
      {
        id: "create",
        title: "Create or import a project",
        blurb: "Start a new estimate from a preset, or let AI build one from a contractor proposal.",
        steps: [
          "From the Projects screen, click “New Project” and pick a markup preset and the divisions you expect to use.",
          "Or click “AI Import” and drop in a proposal, bid tab, or scope sheet — AI parses it into a structured CSI estimate you can refine.",
          "Use the “Sandbox” to explore a fully worked demo project without affecting your data.",
        ],
        tips: [
          "The Get Started checklist in the sidebar tracks the first four things to set up: name, a line item, a package, and markup.",
          "Every change autosaves; the indicator near the top shows “Saving… / Saved”.",
        ],
      },
    ],
  },
  {
    id: "estimate",
    label: "Building an Estimate",
    articles: [
      {
        id: "lineitems",
        title: "Divisions, subcategories & line items",
        blurb: "The estimate is organized by CSI MasterFormat division → subcategory → line item.",
        steps: [
          "Pick a division in the left sidebar to load its subcategories.",
          "Click “+ Add Line Item” in a subcategory to add a row.",
          "Fill in description, quantity, unit, and unit cost — the line total and division total update instantly.",
          "Type math directly into a qty or cost cell (e.g. “12*4.5”) and it evaluates, keeping the formula for later edits.",
        ],
        tips: [
          "Press Enter in a cell to add another row; Tab moves across cells.",
          "Show or hide columns with the column menu to match how you like to work.",
        ],
      },
      {
        id: "confidence",
        title: "Cost confidence & alternates",
        blurb: "Tag every line by how solid the number is, and isolate add/deduct alternates.",
        steps: [
          "Set each line’s Status: Hard Bid (firm sub price), Allowance (lump/TBD), or Needs Review (unverified).",
          "Mark a line as an Alternate (Add or Deduct) to keep it out of the base total until it’s accepted.",
          "Roll-ups show your exposure by confidence, so you always know how firm the estimate is.",
        ],
        tips: ["Color-coded rows make low-confidence scope obvious at a glance."],
      },
      {
        id: "markup",
        title: "Markup & totals",
        blurb: "Subtotal → overhead % → profit % → contingency % → total.",
        steps: [
          "Open the Properties tab to set overhead, profit, and contingency percentages.",
          "Override markup per division when a trade carries different rates.",
          "The Summary tab shows the full build-up and cost per SF.",
        ],
      },
    ],
  },
  {
    id: "bids",
    label: "Bids & Buyout",
    articles: [
      {
        id: "packages",
        title: "Trade scopes & collecting bids",
        blurb: "Group scope into packages, then collect competing subcontractor bids against each.",
        steps: [
          "In the Bids tab, create a trade scope (one or more divisions).",
          "Import a bid (AI-parsed or PDF attachment), or enter line items manually.",
          "Each package shows a pipeline: Setup → Collect → Level → Award, so you always know what’s next.",
        ],
        tips: ["Imported bid lines are auto-matched to your estimate items where the description is a clear match."],
      },
      {
        id: "leveling",
        title: "Leveling & comparing",
        blurb: "Put bidders on equal footing before you compare.",
        steps: [
          "Open a package’s Compare view to see each bidder side by side against your budget.",
          "Click “+ Standard Checklist” to add the items every GC levels (general conditions, bonds, insurance, cleanup, hoisting, tax, permits, escalation).",
          "Mark which items each bidder included and add leveling amounts for what they didn’t.",
          "The plug-vs-bid banner shows budget vs the low leveled bid and whether you’re over or under.",
        ],
      },
      {
        id: "award",
        title: "Awarding & the Buyout log",
        blurb: "Award in one click — the contract flows straight into Buyout.",
        steps: [
          "On the comparison, click “Award” next to a bidder (the low leveled bid is starred).",
          "OpenEstimate creates an awarded Buyout row with the leveled total as the contract amount, vendor, and a link back to the winning bid.",
          "Track contract numbers, dates, and status in the Buyout tab.",
        ],
        tips: ["Awarded packages turn green in the package pipeline."],
      },
    ],
  },
  {
    id: "project",
    label: "Scope, Schedule & More",
    articles: [
      {
        id: "sow",
        title: "Scope of work, submittals & RFIs",
        blurb: "Generate bidder-ready documents and track open questions.",
        steps: [
          "Use “Scope of Work / Bid Form” on a package to generate a scope sheet for bidders.",
          "The Submittals tab auto-populates from your estimate and supports templates.",
          "Log RFIs and track their cost/schedule impact in the RFI tab.",
        ],
      },
      {
        id: "schedule",
        title: "Schedule & cash flow",
        blurb: "A preliminary schedule and cash-flow curve built from your estimate.",
        steps: [
          "The Schedule tab builds bars from your costed subcategories — group by package, phase, or division.",
          "The Cash Flow tab projects spend over the project duration as an S-curve.",
        ],
      },
    ],
  },
  {
    id: "insights",
    label: "Portfolio Insights",
    articles: [
      {
        id: "insights",
        title: "Analytics across all your projects",
        blurb: "Open “Portfolio Insights” from the user menu — five lenses on your own history.",
        steps: [
          "Portfolio: total contract value, blended margin, and $/SF across every project.",
          "Compare Projects: rank and compare projects by $/SF and by division.",
          "Estimate Accuracy: budget against awarded contract, by division, on the buyout rows you have recorded. Partial logs read high-variance — the figure is only worth much once a division is close to fully entered.",
          "Trade Partners: a scorecard per firm — how often they bid, win, and how they land vs budget.",
          "Cost Library: search any item to see every time you’ve priced it, with project, size, and date.",
        ],
        tips: ["Insights fill in as you award bids and complete projects — the more you use OpenEstimate, the sharper they get."],
      },
    ],
  },
  {
    id: "data",
    label: "Saving, History & Sharing",
    articles: [
      {
        id: "snapshots",
        title: "Snapshots & history",
        blurb: "Freeze a version of the estimate and compare versions over time.",
        steps: [
          "Create a snapshot to lock the current estimate (e.g. at SD, DD, CD milestones).",
          "Compare any two snapshots in the Analysis tab to see what changed.",
        ],
      },
      {
        id: "collab",
        title: "Collaboration & sharing",
        blurb: "Multiple estimators can work the same project, with roles.",
        steps: [
          "Share a project and assign a role: Owner, Admin, Editor, or Viewer.",
          "Edits sync live; if a teammate changes a cell you just edited, you’ll see a heads-up.",
          "The activity log records who changed what and when, down to the field.",
        ],
      },
      {
        id: "recovery",
        title: "Autosave & recovery",
        blurb: "Your work is saved continuously and backed up locally.",
        tips: [
          "Cloud projects autosave a couple of seconds after each change.",
          "A local backup runs every few minutes; if a newer backup is found on load, you’ll be offered a one-click restore.",
        ],
      },
    ],
  },
  {
    id: "shortcuts",
    label: "Keyboard Shortcuts",
    articles: [
      {
        id: "shortcuts",
        title: "Keyboard shortcuts",
        blurb: "Move faster with the keyboard.",
        tips: [
          "Ctrl/⌘ + Z — undo (covers structural changes and committed cell edits)",
          "Ctrl/⌘ + Y — redo",
          "Enter — add a new line item from the current row",
          "Tab / Shift+Tab — move across cells",
          "Ctrl/⌘ + F — search the estimate",
          "? — show the shortcuts overlay",
        ],
      },
    ],
  },
];

export default function HelpCenter({ T, onClose }: { T: Theme; onClose: () => void }) {
  const [activeId, setActiveId] = useState<string>(SECTIONS[0].id);
  const [q, setQ] = useState("");

  const ql = q.trim().toLowerCase();
  const matches = (a: Article) =>
    !ql ||
    a.title.toLowerCase().includes(ql) ||
    a.blurb.toLowerCase().includes(ql) ||
    (a.steps || []).some((s) => s.toLowerCase().includes(ql)) ||
    (a.tips || []).some((s) => s.toLowerCase().includes(ql));

  const visibleSections = useMemo(() => {
    if (!ql) return SECTIONS.filter((s) => s.id === activeId);
    return SECTIONS
      .map((s) => ({ ...s, articles: s.articles.filter(matches) }))
      .filter((s) => s.articles.length > 0);
  }, [ql, activeId]);

  const navSections = ql ? SECTIONS.filter((s) => s.articles.some(matches)) : SECTIONS;

  const QBadge = (
    <span style={{ width: 22, height: 22, borderRadius: "50%", border: `1.5px solid ${T.textInverse}`, color: T.textInverse, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>?</span>
  );

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: "min(960px, 100%)", height: "min(680px, 92vh)", background: T.bgApp, border: `1px solid ${T.borderPrimary}`, borderRadius: 10, overflow: "hidden", display: "flex", flexDirection: "column", fontFamily: FONT, boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", borderBottom: `1px solid ${T.borderPrimary}`, background: T.bgHeader }}>
          {QBadge}
          <div style={{ fontSize: 14, fontWeight: 700, color: T.textInverse }}>Help &amp; Guides</div>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search help…"
            style={{ marginLeft: "auto", width: 260, fontSize: 12, padding: "7px 11px", background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, color: T.textPrimary, fontFamily: FONT, outline: "none" }} />
          <button onClick={onClose} title="Close" style={{ background: "none", border: "none", color: T.textInverse, cursor: "pointer", padding: 4, display: "flex" }}><Icon.X size={18} /></button>
        </div>

        <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
          <div style={{ width: 220, flexShrink: 0, borderRight: `1px solid ${T.borderPrimary}`, background: T.bgPanel, overflowY: "auto", padding: "8px 0" }}>
            {navSections.map((s) => {
              const active = !ql && s.id === activeId;
              return (
                <button key={s.id} onClick={() => { setActiveId(s.id); setQ(""); }}
                  style={{ width: "100%", textAlign: "left", padding: "9px 16px", fontSize: 12, fontWeight: active ? 700 : 500, fontFamily: FONT, cursor: "pointer",
                    background: active ? T.bgAccentFaint : "transparent", border: "none", borderLeft: `2px solid ${active ? T.borderAccent : "transparent"}`,
                    color: active ? T.borderAccent : T.textTertiary }}>
                  {s.label}
                </button>
              );
            })}
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
            {visibleSections.length === 0 ? (
              <div style={{ color: T.textWeak, fontSize: 13, padding: "40px 0", textAlign: "center" }}>No help topics match “{q}”.</div>
            ) : (
              visibleSections.map((s) => (
                <div key={s.id}>
                  {ql && <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: T.textWeak, margin: "4px 0 10px" }}>{s.label}</div>}
                  {s.articles.map((a) => (
                    <div key={a.id} style={{ marginBottom: 26 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary, marginBottom: 4 }}>{a.title}</div>
                      <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 12, lineHeight: 1.5 }}>{a.blurb}</div>
                      {a.steps && (
                        <ol style={{ margin: "0 0 12px", paddingLeft: 20, display: "flex", flexDirection: "column", gap: 7 }}>
                          {a.steps.map((step, i) => (
                            <li key={i} style={{ fontSize: 12.5, color: T.textTertiary, lineHeight: 1.55 }}>{step}</li>
                          ))}
                        </ol>
                      )}
                      {a.tips && (
                        <div style={{ background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 10, padding: "10px 14px" }}>
                          {a.tips.map((tip, i) => (
                            <div key={i} style={{ display: "flex", gap: 8, fontSize: 12, color: T.textTertiary, lineHeight: 1.5, padding: "3px 0" }}>
                              <span style={{ color: T.borderAccent, flexShrink: 0 }}>›</span>
                              <span>{tip}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
